import { API_PROXY_PATH } from "../edge/apiProxy";
import HttpError from "../types/HttpError";
import { keysToCamel, keysToSnake } from "./casing";
import { drainQueue, enqueue } from "./offline";

// localhost, not 127.0.0.1: the dev UI runs on localhost, and the browser
// only sends the API's SameSite=Lax session cookies to the same site. For
// the same reason a Pages preview (<branch>.khipur.pages.dev) calls the API
// through its own origin's /api (src/edge/apiProxy.ts), its builds marked
// by REACT_APP_API_PROXY.
export const API_URL = process.env.REACT_APP_API_PROXY
  ? `${window.location.origin}${API_PROXY_PATH}`
  : process.env.REACT_APP_API_URL || "http://localhost:5000";

// The session lives in HttpOnly cookies the app can't read (ADR 0014). This
// flag only remembers that there is one, so a returning visit knows to ask
// /auth/me and an anonymous one doesn't. It is not a credential.
const SESSION_HINT_KEY = "khipur_signed_in";

export const hasSessionHint = (): boolean =>
  localStorage.getItem(SESSION_HINT_KEY) !== null;
export const setSessionHint = (): void =>
  localStorage.setItem(SESSION_HINT_KEY, "1");
export const clearSessionHint = (): void =>
  localStorage.removeItem(SESSION_HINT_KEY);

/**
 * Sent on every write. The API refuses writes without it (CSRF): a cross-site
 * form can't set it, and a cross-site fetch that does needs a CORS preflight
 * only our origins pass. Reads don't need it.
 */
export const CLIENT_HEADER = { "X-Khipur-Client": "web" };

// The signed-in user, as AuthContext last stored it.
export const USER_KEY = "khipur_user";

/**
 * Names the signed-in user on API calls. The service worker can't see the
 * HttpOnly session cookies, so it keys its offline copies of API responses by
 * this instead, and one account never gets another's shelf. It is not a
 * credential; the API ignores it.
 */
export const USER_HEADER = "X-Khipur-User";
const sessionUserId = (): number | null => {
  try {
    return JSON.parse(localStorage.getItem(USER_KEY) ?? "null")?.id ?? null;
  } catch {
    return null;
  }
};

export class ApiError extends Error {
  status: number;
  body: HttpError | null;

  constructor(status: number, body: HttpError | null) {
    super(body?.code || `HTTP ${status}`);
    this.status = status;
    this.body = body;
  }
}

/** Error code for a mutation that was queued because the network was down. */
export const OFFLINE_QUEUED_CODE = "QUEUED_OFFLINE";

export const isOfflineQueued = (err: unknown): boolean =>
  err instanceof ApiError && err.body?.code === OFFLINE_QUEUED_CODE;

type Options = {
  method?: string;
  body?: unknown;
  auth?: boolean;
  // When an offline change was made, sent as it's replayed (ADR 0016).
  queuedAt?: string;
  // Kept for later when the network can't be reached (`queued`).
  queue?: boolean;
  // A GET the device's stored copy may answer at once, refreshed behind it
  // (public/service-worker.js); src/api/updates.ts hears when it changes.
  stored?: boolean;
};

/** The time an offline change was made; the API records it as when it happened. */
export const QUEUED_AT_HEADER = "X-Queued-At";

async function request<T>(path: string, opts: Options): Promise<T> {
  const { method = "GET", body, auth, queuedAt, queue, stored } = opts;
  const headers: Record<string, string> =
    method === "GET" ? {} : { ...CLIENT_HEADER };

  // A form (an upload) goes as it is: the browser sets its multipart type.
  const form = body instanceof FormData;
  if (body !== undefined && !form) {
    headers["Content-Type"] = "application/json";
  }

  if (queuedAt) {
    headers[QUEUED_AT_HEADER] = queuedAt;
  }
  const userId = auth ? sessionUserId() : null;
  if (userId !== null) {
    headers[USER_HEADER] = String(userId);
  }

  let response: Response;
  try {
    response = await fetch(`${API_URL}${path}`, {
      method,
      headers,
      // Session cookies ride along on signed-in calls; public catalog calls
      // (auth = false) go without them.
      credentials: auth ? "include" : "omit",
      ...(stored && { cache: "force-cache" as RequestCache }),
      body: form
        ? body
        : body !== undefined
          ? JSON.stringify(keysToSnake(body))
          : undefined,
    });
  } catch (err) {
    // Network unreachable. A write sent as `queued` (a shelf change, ADR
    // 0005's offline tolerance) waits for replay; everything else
    // propagates the failure.
    if (queue) {
      enqueue({ path, method, body, userId: sessionUserId() });
      throw new ApiError(0, {
        code: OFFLINE_QUEUED_CODE,
        errors: ["Saved offline — will sync when you're back online."],
      });
    }
    throw err;
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const data = await response.json().catch(() => null);

  if (!response.ok) {
    // Error bodies are camelCased too: `code` and `errors` read the same,
    // and a "moved" 404's `work_id` becomes `workId`.
    throw new ApiError(response.status, keysToCamel<HttpError | null>(data));
  }

  return keysToCamel<T>(data);
}

/**
 * Replay queued offline mutations, oldest first. Safe to call any time —
 * a no-op when the queue is empty. If the network is still down, `request`
 * re-queues each mutation as it fails, so nothing is lost; a server
 * rejection (4xx/5xx) drops the entry since it can never succeed later.
 * Only the signed-in user's mutations are replayed.
 */
export const flushQueue = async (): Promise<void> => {
  for (const mutation of drainQueue(sessionUserId())) {
    try {
      await request(mutation.path, {
        method: mutation.method,
        body: mutation.body,
        auth: true,
        queuedAt: mutation.queuedAt,
        queue: true,
      });
    } catch {
      // Handled above: re-queued on network failure, dropped on rejection.
    }
  }
};

export const api = {
  get: <T>(path: string, auth = true, stored = false) =>
    request<T>(path, { auth, stored }),
  post: <T>(path: string, body: unknown, auth = true) =>
    request<T>(path, { method: "POST", body, auth }),
  patch: <T>(path: string, body: unknown, auth = true) =>
    request<T>(path, { method: "PATCH", body, auth }),
  put: <T>(path: string, body: unknown, auth = true) =>
    request<T>(path, { method: "PUT", body, auth }),
  del: <T>(path: string, auth = true, body?: unknown) =>
    request<T>(path, { method: "DELETE", auth, body }),
};

/**
 * Writes that wait for the network when it can't be reached, replayed by
 * `flushQueue`: the shelf's changes only (`src/data/library.ts`; the owner's
 * call in plan 019), never a comment, a like or a follow, which others see
 * and which mean nothing hours late.
 */
export const queued = {
  post: <T>(path: string, body: unknown) =>
    request<T>(path, { method: "POST", body, auth: true, queue: true }),
  patch: <T>(path: string, body: unknown) =>
    request<T>(path, { method: "PATCH", body, auth: true, queue: true }),
  put: <T>(path: string, body: unknown) =>
    request<T>(path, { method: "PUT", body, auth: true, queue: true }),
  del: <T>(path: string) =>
    request<T>(path, { method: "DELETE", auth: true, queue: true }),
};
